import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import { interactiveHoverVariants, modalVariants } from '@/animations';
import { StaggerItem, StaggerReveal } from '@/components/Reveal';
import { NAV_CONTENT } from '@/content';
import { Icon } from '@/utils/icons';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (query.trim()) {
        void navigate('/search', { state: { q: query.trim() } });
        onClose();
      }
    },
    [query, navigate, onClose]
  );

  const handlePopularSearch = useCallback(
    (term: string) => {
      setQuery(term);
      void navigate('/search', { state: { q: term } });
      onClose();
    },
    [navigate, onClose]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-[100] flex items-start justify-center p-4 outline-none sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Search">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-neutral-900/40 backdrop-blur-md"
            onClick={onClose}
          />

          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={modalVariants}
            className="relative mt-20 w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"
            onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <form onSubmit={handleSearch} className="flex items-center gap-4">
                <Icon name="Search" className="text-primary-600" size={24} />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={NAV_CONTENT.actions.search.placeholder}
                  className="flex-1 bg-transparent text-lg font-medium text-neutral-800 outline-none placeholder:text-neutral-300"
                  autoFocus
                  aria-label={NAV_CONTENT.actions.search.placeholder}
                />
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  aria-label="Close search">
                  <Icon name="X" size={20} />
                </button>
              </form>

              <div className="mt-2 border-t border-neutral-100 pt-4">
                <h4 className="mb-4 text-xs font-black tracking-widest text-neutral-400 uppercase">
                  {NAV_CONTENT.search.popularSearchesTitle}
                </h4>
                <StaggerReveal className="flex flex-wrap gap-2">
                  {NAV_CONTENT.search.popularSearches.map(term => (
                    <StaggerItem key={term.id}>
                      <motion.button
                        type="button"
                        whileHover="hover"
                        whileTap="tap"
                        variants={interactiveHoverVariants}
                        onClick={() => handlePopularSearch(term.label)}
                        className="hover:bg-primary-50 hover:text-primary-600 rounded-2xl bg-neutral-50 px-4 py-2 text-sm font-bold text-neutral-600 transition-colors">
                        {term.label}
                      </motion.button>
                    </StaggerItem>
                  ))}
                </StaggerReveal>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(SearchModal);
